/**
 * Top-level authentication gate component.
 *
 * Routes the user to the appropriate view based on auth state:
 * - loading: shows a loading spinner
 * - onboarding: shows the wallet creation flow
 * - locked: shows the lock screen for PIN entry
 * - unlocked: renders children (the main app)
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module AuthGate
 */

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthProvider';
import { useRestoreEvolu } from '@/features/auth/hooks/useRestoreEvolu';
import { WelcomeScreen } from '@/features/onboarding/components/WelcomeScreen';
import { PinSetup } from '@/components/ui/pin-setup';
import RestorePage from '@/features/onboarding/pages/RestorePage';
import ForgotPinPage from '@/features/auth/pages/ForgotPinPage';
import { LockScreen } from './LockScreen';
import { OnboardingFlow } from './OnboardingFlow';

/** Props for AuthGate component. */
interface AuthGateProps {
  /** Children to render when the user is authenticated (unlocked). */
  readonly children: ReactNode;
}

/** Onboarding sub-states managed within AuthGate. */
type OnboardingStep = 'welcome' | 'create' | 'restore';

/** Lock screen sub-states: PIN entry or forgot PIN recovery. */
type LockSubState = 'pin_entry' | 'forgot_pin';

/**
 * Authentication gate that wraps the entire app.
 *
 * Renders the appropriate screen based on auth state:
 * loading, onboarding, locked, or the main app (children).
 *
 * Tier: All tiers (core infrastructure).
 */
export function AuthGate({ children }: AuthGateProps): ReactNode {
  const { state: authState, completeRestore } = useAuth();
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>('welcome');
  const [lockSubState, setLockSubState] = useState<LockSubState>('pin_entry');
  const { restoreEvolu } = useRestoreEvolu();

  const handleCreateWallet = useCallback((): void => {
    setOnboardingStep('create');
  }, []);

  const handleRestoreWallet = useCallback((): void => {
    setOnboardingStep('restore');
  }, []);

  if (authState === 'loading') {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            Loading...
          </p>
        </div>
      </main>
    );
  }

  if (authState === 'onboarding') {
    if (onboardingStep === 'welcome') {
      return (
        <WelcomeScreen onCreateWallet={handleCreateWallet} onRestoreWallet={handleRestoreWallet} />
      );
    }

    if (onboardingStep === 'create') {
      return <OnboardingFlow />;
    }

    // Restore flow: renders RestorePage with PinSetup via render-prop
    return (
      <RestorePage
        onComplete={completeRestore}
        onBack={() => setOnboardingStep('welcome')}
        restoreEvolu={restoreEvolu}
        renderPinSetup={(onPinSet) => <PinSetup onComplete={onPinSet} />}
      />
    );
  }

  if (authState === 'locked') {
    if (lockSubState === 'forgot_pin') {
      return <ForgotPinPage onBack={() => setLockSubState('pin_entry')} />;
    }
    return <LockScreen onForgotPin={() => setLockSubState('forgot_pin')} />;
  }

  // authState === 'unlocked'
  return <>{children}</>;
}

export type { AuthGateProps };
